import { useState, useRef, useCallback } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { COLUMN_ORDER_KEY } from "@/components/tableau/utils/constants";
import {
  Type, Check, Calendar, List, Circle, Euro, Percent, Hash, Phone, Calculator, Image
} from "lucide-react";

const COLUMN_TYPES_WITH_ICONS = [
  { value: 'text', label: 'Texte', icon: Type },
  { value: 'checkbox', label: 'Case à cocher', icon: Check },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'dropdown', label: 'Liste déroulante', icon: List },
  { value: 'status', label: 'Feu tricolore', icon: Circle },
  { value: 'amount', label: 'Montant €', icon: Euro },
  { value: 'percentage', label: 'Pourcentage %', icon: Percent },
  { value: 'numerique', label: 'Numérique', icon: Hash },
  { value: 'phone', label: 'Téléphone', icon: Phone },
  { value: 'calcul', label: 'Calcul', icon: Calculator },
  { value: 'fabric', label: 'Vignette toile', icon: Image },
];

export function useColumnManagement({ columns, setColumns, serverColumnTypes, orders, setOrders }) {
  const [showColumnTypeModal, setShowColumnTypeModal] = useState(false);
  const [selectedColumnForType, setSelectedColumnForType] = useState(null);
  const [columnTypeForm, setColumnTypeForm] = useState({
    type: 'text', dropdownOptions: [],
    statusLabels: { green: 'Validé', orange: 'En cours', red: 'Bloqué' },
    statusDisplay: 'circle_text', decimalPlaces: 2, useThousandsSeparator: true,
    formula: '', calculFormat: 'currency', calculDecimalPlaces: 2
  });
  const [existingValues, setExistingValues] = useState([]);
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, columnName: null });
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [newColumnForm, setNewColumnForm] = useState({ name: '', type: 'text' });
  const [isCreatingColumn, setIsCreatingColumn] = useState(false);
  const formulaInputRef = useRef(null);

  const handleColumnContextMenu = useCallback((e, columnName) => {
    e.preventDefault();
    setContextMenu({ show: true, x: e.clientX, y: e.clientY, columnName });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu({ show: false, x: 0, y: 0, columnName: null });
  }, []);

  const openColumnTypeModal = useCallback(async (columnName) => {
    closeContextMenu();
    const col = columns.find(c => c.name === columnName);
    setSelectedColumnForType(columnName);
    setColumnTypeForm({
      type: col?.type || 'text',
      dropdownOptions: col?.dropdown_options || [],
      statusLabels: col?.status_labels || { green: 'Validé', orange: 'En cours', red: 'Bloqué' },
      statusDisplay: col?.status_display || 'circle_text',
      decimalPlaces: col?.decimal_places ?? 2,
      useThousandsSeparator: col?.use_thousands_separator !== false,
      formula: col?.formula || '',
      calculFormat: col?.calcul_format || 'currency',
      calculDecimalPlaces: col?.calcul_decimal_places ?? 2
    });
    try {
      const response = await authFetch(`${API}/columns/${encodeURIComponent(columnName)}/values`);
      if (response.ok) { const data = await response.json(); setExistingValues(data.values || []); }
    } catch (e) { console.error("Failed to fetch values:", e); }
    setShowColumnTypeModal(true);
  }, [columns, closeContextMenu]);

  const handleSaveColumnType = useCallback(async () => {
    if (!selectedColumnForType) return;
    try {
      const response = await authFetch(`${API}/columns/${encodeURIComponent(selectedColumnForType)}/type`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          column_name: selectedColumnForType, column_type: columnTypeForm.type,
          dropdown_options: columnTypeForm.type === 'dropdown' ? columnTypeForm.dropdownOptions : null,
          status_labels: columnTypeForm.type === 'status' ? columnTypeForm.statusLabels : null,
          status_display: columnTypeForm.type === 'status' ? columnTypeForm.statusDisplay : null,
          decimal_places: columnTypeForm.type === 'numerique' ? columnTypeForm.decimalPlaces : null,
          use_thousands_separator: columnTypeForm.type === 'numerique' ? columnTypeForm.useThousandsSeparator : null,
          formula: columnTypeForm.type === 'calcul' ? columnTypeForm.formula : null,
          calcul_format: columnTypeForm.type === 'calcul' ? columnTypeForm.calculFormat : null,
          calcul_decimal_places: columnTypeForm.type === 'calcul' ? columnTypeForm.calculDecimalPlaces : null
        })
      });
      if (response.ok) {
        const typeConfig = {
          type: columnTypeForm.type,
          dropdown_options: columnTypeForm.type === 'dropdown' ? columnTypeForm.dropdownOptions : undefined,
          status_labels: columnTypeForm.type === 'status' ? columnTypeForm.statusLabels : undefined,
          status_display: columnTypeForm.type === 'status' ? columnTypeForm.statusDisplay : undefined,
          decimal_places: columnTypeForm.type === 'numerique' ? columnTypeForm.decimalPlaces : undefined,
          use_thousands_separator: columnTypeForm.type === 'numerique' ? columnTypeForm.useThousandsSeparator : undefined,
          formula: columnTypeForm.type === 'calcul' ? columnTypeForm.formula : undefined,
          calcul_format: columnTypeForm.type === 'calcul' ? columnTypeForm.calculFormat : undefined,
          calcul_decimal_places: columnTypeForm.type === 'calcul' ? columnTypeForm.calculDecimalPlaces : undefined
        };
        await authFetch(`${API}/table-config/types/${encodeURIComponent(selectedColumnForType)}`, {
          method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(typeConfig)
        });
        setColumns(prev => prev.map(col =>
          col.name === selectedColumnForType ? { ...col, ...typeConfig } : col
        ));
        toast.success("Type de colonne mis à jour");
        setShowColumnTypeModal(false);
      } else { const err = await response.json(); toast.error(err.detail || "Erreur"); }
    } catch { toast.error("Erreur lors de la mise à jour"); }
  }, [selectedColumnForType, columnTypeForm, setColumns]);

  const addDropdownOption = useCallback((option) => {
    if (option && !columnTypeForm.dropdownOptions.includes(option)) {
      setColumnTypeForm(prev => ({ ...prev, dropdownOptions: [...prev.dropdownOptions, option] }));
    }
  }, [columnTypeForm.dropdownOptions]);

  const removeDropdownOption = useCallback((option) => {
    setColumnTypeForm(prev => ({ ...prev, dropdownOptions: prev.dropdownOptions.filter(o => o !== option) }));
  }, []);

  const addAllExistingValues = useCallback(() => {
    setColumnTypeForm(prev => ({ ...prev, dropdownOptions: [...new Set([...prev.dropdownOptions, ...existingValues])] }));
  }, [existingValues]);

  const handleDeleteColumn = useCallback(async (columnName) => {
    closeContextMenu();
    const confirmed = window.confirm(`⚠ Supprimer la colonne "${columnName}" ?\n\nCette action supprimera :\n- La colonne du tableau\n- Toutes les données associées dans chaque commande\n\nCette action est irréversible.`);
    if (!confirmed) return;
    try {
      toast.info("Suppression en cours...");
      const response = await authFetch(`${API}/columns/${encodeURIComponent(columnName)}`, { method: "DELETE" });
      if (response.ok) {
        const data = await response.json();
        setColumns(prev => prev.filter(col => col.name !== columnName));
        const savedOrder = localStorage.getItem(COLUMN_ORDER_KEY);
        if (savedOrder) { localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(JSON.parse(savedOrder).filter(n => n !== columnName))); }
        setShowColumnTypeModal(false);
        toast.success(`Colonne "${columnName}" supprimée (${data.orders_updated} commandes mises à jour)`);
      } else { const err = await response.json().catch(() => ({})); toast.error(err.detail || `Erreur (${response.status})`); }
    } catch (error) { toast.error("Erreur réseau lors de la suppression de la colonne"); }
  }, [closeContextMenu, setColumns]);

  const handleCreateColumn = useCallback(async () => {
    if (!newColumnForm.name.trim()) { toast.error("Le nom de la colonne est requis"); return; }
    setIsCreatingColumn(true);
    try {
      const response = await authFetch(`${API}/columns`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newColumnForm.name.trim(), type: newColumnForm.type })
      });
      if (response.ok) {
        const data = await response.json();
        setColumns(prev => [...prev, data.column]);
        setNewColumnForm({ name: '', type: 'text' });
        setShowAddColumnModal(false);
        toast.success(`Colonne "${data.column.name}" créée`);
      } else { const err = await response.json().catch(() => ({})); toast.error(err.detail || "Erreur"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setIsCreatingColumn(false); }
  }, [newColumnForm, setColumns]);

  const getColumnType = useCallback((columnName) => {
    const col = columns.find(c => c.name === columnName);
    if (col?.type) return col.type;
    const serverConfig = serverColumnTypes[columnName];
    return serverConfig?.type || 'text';
  }, [columns, serverColumnTypes]);

  const getColumnDropdownOptions = useCallback((columnName) => {
    const col = columns.find(c => c.name === columnName);
    if (col?.dropdown_options) return col.dropdown_options;
    const serverConfig = serverColumnTypes[columnName];
    return serverConfig?.dropdown_options || [];
  }, [columns, serverColumnTypes]);

  const getColumnStatusConfig = useCallback((columnName) => {
    const col = columns.find(c => c.name === columnName);
    if (col?.status_labels || col?.status_display) {
      return { labels: col?.status_labels || { green: 'Validé', orange: 'En cours', red: 'Bloqué' }, display: col?.status_display || 'circle_text' };
    }
    const serverConfig = serverColumnTypes[columnName];
    return { labels: serverConfig?.status_labels || { green: 'Validé', orange: 'En cours', red: 'Bloqué' }, display: serverConfig?.status_display || 'circle_text' };
  }, [columns, serverColumnTypes]);

  const getStatusColor = useCallback((value, columnName) => {
    if (!value) return null;
    const config = getColumnStatusConfig(columnName);
    const valueLower = String(value).toLowerCase().trim();
    for (const [colorKey, label] of Object.entries(config.labels)) {
      if (valueLower === label.toLowerCase() || valueLower === colorKey) return colorKey;
    }
    if (['green', 'vert', 'validé', 'ok', 'oui', 'yes', 'done', 'terminé', 'complet', 'payé', 'réglé', 'encaissé'].includes(valueLower)) return 'green';
    if (['orange', 'en cours', 'pending', 'attente', 'warning', 'partiel', 'acompte'].includes(valueLower)) return 'orange';
    if (['red', 'rouge', 'bloqué', 'erreur', 'error', 'non', 'no', 'ko', 'impayé', 'refusé'].includes(valueLower)) return 'red';
    return null;
  }, [getColumnStatusConfig]);

  const renderStatusCell = useCallback((value, columnName) => {
    const config = getColumnStatusConfig(columnName);
    const colorKey = getStatusColor(value, columnName);
    if (!colorKey) return <span className="text-zinc-300 italic">vide</span>;
    const colorClasses = { green: 'bg-green-500', orange: 'bg-orange-500', red: 'bg-red-500' };
    const label = config.labels[colorKey] || colorKey;
    if (config.display === 'circle') {
      return <div className="flex justify-center"><div className={`w-4 h-4 rounded-full ${colorClasses[colorKey]}`} title={label} /></div>;
    }
    return <div className="flex items-center gap-2"><div className={`w-3 h-3 rounded-full ${colorClasses[colorKey]} flex-shrink-0`} /><span className="truncate">{label}</span></div>;
  }, [getColumnStatusConfig, getStatusColor]);

  return {
    showColumnTypeModal, setShowColumnTypeModal, selectedColumnForType, columnTypeForm, setColumnTypeForm,
    existingValues, contextMenu, showAddColumnModal, setShowAddColumnModal,
    newColumnForm, setNewColumnForm, isCreatingColumn, formulaInputRef,
    COLUMN_TYPES_WITH_ICONS,
    handleColumnContextMenu, closeContextMenu, openColumnTypeModal,
    handleSaveColumnType, addDropdownOption, removeDropdownOption, addAllExistingValues,
    handleDeleteColumn, handleCreateColumn,
    getColumnType, getColumnDropdownOptions, getColumnStatusConfig, getStatusColor, renderStatusCell,
  };
}
